import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Home, Building2, TrendingUp, MessageCircle, CalendarCheck, FileText, Eye, Sparkles, Calculator, Package } from 'lucide-react';
import { api, friendlyError } from '../lib/api';
import SEO from '../components/SEO';

const WINDOWS = [
  { key: '7d', label: '7 días' },
  { key: '30d', label: '30 días' },
  { key: '90d', label: '90 días' },
  { key: 'this_month', label: 'Este mes' },
  { key: 'last_month', label: 'Mes anterior' },
  { key: 'historic', label: 'Histórico' },
];

const REL = {
  reliable: { label: 'Confiable', cls: 'bg-emerald-100 text-emerald-800' },
  partial: { label: 'Parcial', cls: 'bg-amber-100 text-amber-800' },
  approximation: { label: 'Aproximación', cls: 'bg-gray-200 text-gray-600' },
};
const ICONS = {
  unique_visitors: Users, property_views: Eye, new_registrations: Users, advisor_recs: Sparkles,
  valuations: Calculator, users_total: Users, active_properties: Home, new_inventory: Package,
  professionals: Building2, whatsapp_clicks: MessageCircle, contact_forms: FileText, visit_requests: CalendarCheck,
};

function RelBadge({ r }) {
  const cfg = REL[r] || REL.approximation;
  return <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${cfg.cls}`} data-testid={`rel-${r}`}>{cfg.label}</span>;
}

function Metric({ mkey, label, value, extra, detail, reliability, Icon }) {
  const I = Icon || ICONS[mkey] || TrendingUp;
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4" data-testid={`metric-${mkey}`}>
      <div className="flex items-center justify-between">
        <I className="w-5 h-5 text-habita" />
        <RelBadge r={reliability} />
      </div>
      <p className="mt-2 text-3xl font-black text-habita-dark" data-testid={`metric-${mkey}-value`}>
        {Number(value).toLocaleString('es-CO')}{extra != null && <span className="text-base text-gray-400"> · {extra} unid.</span>}
      </p>
      <p className="text-sm text-gray-500">{label}</p>
      {detail && (
        <p className="text-xs text-gray-400 mt-1">
          {Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join(' · ')}
        </p>
      )}
    </div>
  );
}

export default function AdminGrowthV2() {
  const [window, setWindow] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (w) => {
    setLoading(true); setError('');
    try { setData(await api.growthV2(w)); }
    catch (e) { setError(friendlyError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(window); }, [load, window]);

  const leads = data?.leads;
  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="growth-v2">
      <SEO title="Growth v2 · Habita" description="Dashboard de crecimiento" />
      <Link to="/admin/dashboard" className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full bg-white border border-emerald-600 text-habita-dark font-semibold hover:bg-habita hover:text-white transition-colors">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </Link>
      <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-serif text-3xl font-black text-habita-dark">Growth v2</h1>
        <div className="flex flex-wrap gap-1" data-testid="window-selector">
          {WINDOWS.map((w) => (
            <button key={w.key} onClick={() => setWindow(w.key)} data-testid={`window-${w.key}`}
              className={`text-sm px-3 h-9 rounded-full border transition ${window === w.key ? 'bg-habita text-white border-habita' : 'bg-white text-gray-600 border-gray-200 hover:border-habita'}`}>
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="min-h-[40vh] flex items-center justify-center"><div className="w-10 h-10 border-4 border-habita border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <p className="text-rose-600 mt-8" data-testid="growth-v2-error">{error}</p>
      ) : (
        <>
          {/* KPIs primarios de LEADS separados */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="rounded-2xl border-2 border-emerald-600 bg-emerald-50 p-5" data-testid="kpi-identified-buyers">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-emerald-800 font-bold">Compradores identificados</span>
                <RelBadge r={leads.identified_buyers?.reliability || 'reliable'} />
              </div>
              <p className="text-4xl font-black text-habita-dark mt-1" data-testid="kpi-identified-buyers-value">{(leads.identified_buyers?.value || 0).toLocaleString('es-CO')}</p>
              <p className="text-xs text-gray-500 mt-1">Personas únicas vía Advisor</p>
            </div>
            <div className="rounded-2xl border-2 border-habita bg-habita-mint/30 p-5" data-testid="kpi-leads-buyer">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-habita-dark font-bold">Intenciones (comprador)</span>
                <RelBadge r={leads.buyer.reliability} />
              </div>
              <p className="text-4xl font-black text-habita-dark mt-1" data-testid="kpi-leads-buyer-value">{leads.buyer.value.toLocaleString('es-CO')}</p>
              <p className="text-xs text-gray-500 mt-1">Interacciones, no personas únicas</p>
            </div>
            <div className="rounded-2xl border-2 border-habita bg-habita-mint/30 p-5" data-testid="kpi-leads-seller">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-habita-dark font-bold">Leads vendedores</span>
                <RelBadge r={leads.seller.reliability} />
              </div>
              <p className="text-4xl font-black text-habita-dark mt-1" data-testid="kpi-leads-seller-value">{leads.seller.value.toLocaleString('es-CO')}</p>
              <p className="text-xs text-gray-500 mt-1">Valoraciones de inmueble</p>
            </div>
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-5" data-testid="kpi-leads-total">
              <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Total leads (secundario)</span>
              <p className="text-4xl font-black text-gray-400 mt-1">{leads.total_secondary.value.toLocaleString('es-CO')}</p>
              <p className="text-xs text-gray-400 mt-1">Intenciones + vendedor</p>
            </div>
          </div>

          {/* Compradores identificados · desglose (solo si hay estructura) */}
          {leads.identified_buyers && (
            <>
              <h2 className="font-serif text-lg font-bold text-habita-dark mt-8 mb-3">Compradores identificados · desglose</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="identified-buyers-breakdown">
                <Metric mkey="buyer_compra" label="Intención: Compra" value={leads.identified_buyers.breakdown.compra.value} reliability="reliable" />
                <Metric mkey="buyer_arriendo" label="Intención: Arriendo" value={leads.identified_buyers.breakdown.arriendo.value} reliability="reliable" />
                <Metric mkey="buyer_src_advisor" label="Fuente: Advisor" value={leads.identified_buyers.by_source.advisor.value} reliability="reliable" />
                <Metric mkey="buyer_src_firm" label="Fuente: Advisor + propiedad" value={leads.identified_buyers.by_source.advisor_firm_pick.value} reliability="reliable" />
              </div>
            </>
          )}

          {/* Intenciones comerciales · desglose */}
          <h2 className="font-serif text-lg font-bold text-habita-dark mt-8 mb-3">Intenciones comerciales (comprador)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Metric mkey="whatsapp_clicks" label="Clicks WhatsApp" value={leads.buyer.breakdown.whatsapp_clicks.value} reliability={leads.buyer.breakdown.whatsapp_clicks.reliability} />
            <Metric mkey="contact_forms" label="Formularios de contacto" value={leads.buyer.breakdown.contact_forms.value} reliability={leads.buyer.breakdown.contact_forms.reliability} />
            <Metric mkey="visit_requests" label="Solicitudes de visita" value={leads.buyer.breakdown.visit_requests.value} reliability={leads.buyer.breakdown.visit_requests.reliability} />
          </div>

          {/* Actividad del período 🟩 */}
          <div className="flex items-center gap-2 mt-8 mb-3">
            <span className="w-3 h-3 rounded bg-emerald-400" />
            <h2 className="font-serif text-lg font-bold text-habita-dark">Actividad del período</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {data.activity.map((m) => <Metric key={m.key} mkey={m.key} label={m.label} value={m.value} reliability={m.reliability} />)}
          </div>

          {/* Stock 🟦 */}
          <div className="flex items-center gap-2 mt-8 mb-3">
            <span className="w-3 h-3 rounded bg-blue-500" />
            <h2 className="font-serif text-lg font-bold text-habita-dark">Stock (acumulado)</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.stock.map((m) => <Metric key={m.key} mkey={m.key} label={m.label} value={m.value} extra={m.extra} detail={m.detail} reliability={m.reliability} />)}
          </div>

          <p className="text-xs text-gray-400 mt-8" data-testid="growth-v2-footnote">
            Excluidos de métricas: {data.excluded.internal_admin_accounts} cuenta(s) interna(s) (role=admin) + datos de prueba · Regla: {data.excluded.rule} · Ventana: {data.range.start ? data.range.start.slice(0, 10) : 'inicio'} → {data.range.end.slice(0, 10)}
          </p>
        </>
      )}
    </div>
  );
}
